import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, ShieldAlert, UserCog, Users, Wand2 } from 'lucide-react';
import { ROLES, USER_STATUSES, type AdminUserView } from '@en3/shared';
import { adminApi } from '@/api/admin.api';
import { queryKeys } from '@/api/client';
import { toDisplayMessage } from '@/api/errors';
import { useAuthStatus, useAuthUser } from '@/api/hooks/useAuth';
import { ROUTES } from '@/app/routes';
import { AiCacheTable } from '@/components/admin/AiCacheTable';
import { OverviewCards } from '@/components/admin/OverviewCards';
import { EmptyBlock, ErrorBlock, LoadingBlock, Pagination } from '@/components/common/States';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardBody, CardHeader, CardTitle, Select } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { AuthRequiredNotice } from '@/pages/WordbookPage';

/**
 * 管理后台。
 *
 * **权限是双层的，这一点必须写在界面上**：前端这里只决定"能不能进这个页面"，
 * 真正的边界在后端 —— 每个 `/api/admin/*` 路由都挂了 `requireAuth` + `requireRole('admin')`。
 * 所以非管理员看到的是一句解释（"仅管理员可访问" + 权限在后端校验），而不是一个
 * 假装成"隐藏菜单"的空页面：把后端校验这层说清楚，用户（尤其是被误配成普通账号的
 * 教师）才知道该找谁，而不是反复刷新。
 *
 * 页面分四块，顺序按"管理员最常做的事"排：
 * 1. 总览四组数字（先看整体是否健康）；
 * 2. AI 缓存（出问题时的第一现场，独立组件自管筛选与分页）；
 * 3. 用户管理（改角色/状态）；
 * 4. 内容维护（装载内置教材目录）。
 *
 * 结构上刻意把"登录态/角色判断"与"内容"分成两个组件：判断里只有 `useAuthStatus` /
 * `useAuthUser` 两个 hook，内容组件里才有数据请求。这样未登录时不会建立任何后台查询
 * （否则会先打一个必然 403 的请求再显示"仅管理员可访问"），hook 顺序也不受登录态影响。
 */

const ROLE_LABELS: Record<string, string> = {
  student: '学生',
  teacher: '教师',
  admin: '管理员',
};

const STATUS_LABELS: Record<string, string> = {
  active: '正常',
  deleted: '已注销',
  disabled: '已停用',
};

const USER_PAGE_SIZE = 20;

/**
 * 管理后台页面入口。
 *
 * 只做登录态与角色判断，真正的数据请求都在 `AdminContent` 里 ——
 * 这样未登录/非管理员时不会建立任何必然失败的查询（见文件头说明）。
 */
export function AdminPage() {
  const status = useAuthStatus();
  const user = useAuthUser();

  if (status === 'idle' || status === 'loading') {
    return (
      <section className="mx-auto max-w-3xl px-5 py-10">
        <h1 className="text-2xl font-bold">管理后台</h1>
        <p role="status" className="mt-2 text-sm text-muted-foreground">
          正在恢复登录状态…
        </p>
      </section>
    );
  }

  if (status !== 'authenticated' || !user) {
    return (
      <AuthRequiredNotice
        feature="管理后台"
        description="管理后台需要登录且账号角色为管理员：这里能查看全部用户的账号状态并修改角色，因此不对普通账号开放。"
      />
    );
  }

  if (user.role !== 'admin') {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <h1 className="text-2xl font-bold">管理后台</h1>

        <p className="mt-4 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-3 text-sm text-destructive">
          <ShieldAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span>
            仅管理员可访问。当前账号角色是「{ROLE_LABELS[user.role] ?? user.role}」，
            看得到这一页但拿不到任何数据。
          </span>
        </p>

        <div className="mt-4 space-y-2 text-sm text-muted-foreground">
          <p>
            这一层只是界面上的提示，<strong>真正的权限校验在后端</strong>：
            `/api/admin/*` 的每个接口都要求登录且角色为 `admin`，否则返回 403。
            也就是说，即使绕过这个页面直接调接口，也拿不到别人的数据。
          </p>
          <p>如果你确实需要后台权限，请联系平台管理员把你的角色改为管理员。</p>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to={ROUTES.profile}
            className="inline-flex h-10 items-center rounded-md border px-4 text-sm hover:bg-accent hover:text-accent-foreground"
          >
            回到个人中心
          </Link>
          <Link
            to={ROUTES.textbooks}
            className="inline-flex h-10 items-center rounded-md border px-4 text-sm hover:bg-accent hover:text-accent-foreground"
          >
            去教材目录
          </Link>
        </div>
      </section>
    );
  }

  return <AdminContent />;
}

function AdminContent() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: queryKeys.adminOverview,
    queryFn: () => adminApi.overview(),
  });

  const seed = useMutation({
    mutationFn: () => adminApi.seedTextbooks(),
    onSuccess: async (data) => {
      setNotice(`内置教材目录已装载：教材 ${data.textbooks} 套、单元 ${data.units} 个。这是幂等操作，重复点击不会产生重复数据。`);
      // 目录页与"文章下拉"都读 ['textbooks'] / ['articles']，装载后要一起失效
      await queryClient.invalidateQueries({ queryKey: ['textbooks'] });
      await queryClient.invalidateQueries({ queryKey: ['articles'] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminOverview });
    },
  });

  return (
    <section className="mx-auto max-w-5xl px-5 py-8">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <UserCog aria-hidden className="size-6 text-primary" />
          管理后台
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          总览统计、AI 缓存排查、用户角色与状态管理、内置内容装载。
          所有操作都会落到服务端并在服务端再次校验管理员身份。
        </p>
      </header>

      {notice && (
        <p role="status" className="mt-4 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          {notice}
        </p>
      )}

      {/* 1. 总览 */}
      <div className="mt-5">
        {overview.isPending ? (
          <LoadingBlock label="正在统计…" />
        ) : overview.isError ? (
          <ErrorBlock message={toDisplayMessage(overview.error)} onRetry={() => void overview.refetch()} />
        ) : (
          <OverviewCards overview={overview.data} />
        )}
      </div>

      {/* 2. AI 缓存 */}
      <AiCacheTable className="mt-5" />

      {/* 3. 用户管理 */}
      <AdminUserTable className="mt-5" />

      {/* 4. 内容维护 */}
      <Card className="mt-5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 aria-hidden className="size-4 text-primary" />
            内容维护
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-muted-foreground">
            装载内置教材目录（教材 → 单元 → 文章元数据）。平台只内置公开的目录信息，
            正文由具备使用授权的教师或用户导入，详见{' '}
            <Link to={ROUTES.copyright} className="text-primary underline underline-offset-4">
              版权与数据来源
            </Link>
            。
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              loading={seed.isPending}
              onClick={() => {
                setNotice(null);
                seed.mutate();
              }}
            >
              装载内置教材目录
            </Button>
            <span className="text-xs text-muted-foreground">幂等：已存在的教材与单元只会被更新，不会重复插入。</span>
          </div>
          {seed.isError && (
            <p role="alert" className="text-sm text-destructive">
              装载失败：{toDisplayMessage(seed.error)}
            </p>
          )}
        </CardBody>
      </Card>
    </section>
  );
}

/**
 * 用户管理表。
 *
 * 关键约束：**不能修改自己的角色/状态**。后端会直接返回 400，
 * 但前端也要禁用并给出原因 —— 让用户点了才失败是最差的交互，
 * 何况"把自己降级成学生导致再也进不来后台"是个真实且无法自救的事故。
 *
 * 另一条规矩：**停用/注销他人要二次确认**。角色可以来回改（不丢数据），
 * 但把账号设为 disabled / deleted 会让对方立刻无法登录；下拉框改成即生效的话，
 * 一次误触就能让一个学生当天进不来。设为 active（恢复）不需要确认 ——
 * 恢复不破坏任何东西。
 */
function AdminUserTable({ className }: { className?: string }) {
  const queryClient = useQueryClient();
  const me = useAuthUser();

  const [page, setPage] = useState(1);
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [qText, setQText] = useState('');
  const [q, setQ] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  /** 待确认的停用/注销操作（非破坏性变更不经过它） */
  const [statusChange, setStatusChange] = useState<{ user: AdminUserView; next: string } | null>(null);

  useEffect(() => {
    const next = qText.trim();
    if (next === q) return;
    const timer = window.setTimeout(() => {
      setQ(next);
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [qText, q]);

  const params = useMemo(
    () => ({
      page,
      pageSize: USER_PAGE_SIZE,
      role: role.length > 0 ? role : undefined,
      status: status.length > 0 ? status : undefined,
      q: q.length > 0 ? q : undefined,
    }),
    [page, role, status, q],
  );

  const list = useQuery({
    queryKey: queryKeys.adminUsers(params),
    queryFn: () => adminApi.users(params),
  });

  const update = useMutation({
    mutationFn: (input: { id: string; patch: { role?: string; status?: string } }) =>
      adminApi.updateUser(input.id, input.patch),
    onSuccess: async (data) => {
      setStatusChange(null);
      setNotice(
        `已更新「${data.user.displayName}」：角色 ${ROLE_LABELS[data.user.role] ?? data.user.role}、状态 ${STATUS_LABELS[data.user.status] ?? data.user.status}。`,
      );
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminOverview });
    },
  });

  const items = list.data?.items ?? [];
  const hasFilters = role !== '' || status !== '' || q !== '';

  /** 单次修改中的那一行才显示 loading，其余行保持可交互 */
  const pendingId = update.isPending ? update.variables?.id : undefined;

  /** 恢复为「正常」直接执行；停用/注销先确认。 */
  function requestStatusChange(user: AdminUserView, next: string) {
    if (next === 'active') {
      update.mutate({ id: user.id, patch: { status: next } });
      return;
    }
    setStatusChange({ user, next });
  }

  function renderControls(user: AdminUserView) {
    const isSelf = user.id === me?.id;
    const disabled = isSelf || (update.isPending && pendingId !== user.id);
    return {
      isSelf,
      disabled,
      role: (
        <Select
          value={user.role}
          disabled={disabled}
          aria-label={`修改 ${user.displayName} 的角色`}
          title={isSelf ? '不能修改自己的角色（后端会拒绝）' : undefined}
          onChange={(event) => update.mutate({ id: user.id, patch: { role: event.target.value } })}
        >
          {ROLES.map((item) => (
            <option key={item} value={item}>
              {ROLE_LABELS[item] ?? item}
            </option>
          ))}
        </Select>
      ),
      status: (
        <Select
          value={user.status}
          disabled={disabled}
          aria-label={`修改 ${user.displayName} 的状态`}
          title={isSelf ? '不能停用自己的账号（后端会拒绝）' : undefined}
          onChange={(event) => requestStatusChange(user, event.target.value)}
        >
          {USER_STATUSES.map((item) => (
            <option key={item} value={item}>
              {STATUS_LABELS[item] ?? item}
            </option>
          ))}
        </Select>
      ),
    };
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users aria-hidden className="size-4 text-primary" />
          用户管理
        </CardTitle>
        {me && <span className="text-xs text-muted-foreground">当前登录账号已锁定，不能改自己</span>}
      </CardHeader>

      <CardBody className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <label htmlFor="admin-user-q" className="text-xs font-medium">
              搜索（昵称 / 邮箱）
            </label>
            <div className="relative">
              <Search aria-hidden className="pointer-events-none absolute top-3 left-2.5 size-4 text-muted-foreground" />
              <Input
                id="admin-user-q"
                type="search"
                value={qText}
                placeholder="例如 teacher@"
                className="pl-8"
                onChange={(event) => setQText(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="admin-user-role" className="text-xs font-medium">
              角色
            </label>
            <Select
              id="admin-user-role"
              value={role}
              className="w-full"
              onChange={(event) => {
                setRole(event.target.value);
                setPage(1);
              }}
            >
              <option value="">全部角色</option>
              {ROLES.map((item) => (
                <option key={item} value={item}>
                  {ROLE_LABELS[item] ?? item}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="admin-user-status" className="text-xs font-medium">
              状态
            </label>
            <Select
              id="admin-user-status"
              value={status}
              className="w-full"
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">全部状态</option>
              {USER_STATUSES.map((item) => (
                <option key={item} value={item}>
                  {STATUS_LABELS[item] ?? item}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {notice && (
          <p role="status" className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
            {notice}
          </p>
        )}

        {update.isError && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            更新失败：{toDisplayMessage(update.error)}
          </p>
        )}

        {list.isPending ? (
          <LoadingBlock label="正在加载用户…" />
        ) : list.isError ? (
          <ErrorBlock message={toDisplayMessage(list.error)} onRetry={() => void list.refetch()} />
        ) : items.length === 0 ? (
          <EmptyBlock
            title={hasFilters ? '没有匹配的用户' : '还没有用户'}
            description={hasFilters ? '换个关键词或清空筛选试试。' : undefined}
            action={
              hasFilters ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setRole('');
                    setStatus('');
                    setQText('');
                    setQ('');
                    setPage(1);
                  }}
                >
                  清空筛选
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {/* 宽屏：表格（容器自己横向滚动，不把整页撑宽） */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full border-collapse text-left text-xs">
                <caption className="sr-only">用户列表</caption>
                <thead className="text-muted-foreground">
                  <tr className="border-b">
                    <th scope="col" className="py-2 pr-3 font-medium">用户</th>
                    <th scope="col" className="py-2 pr-3 font-medium">角色</th>
                    <th scope="col" className="py-2 pr-3 font-medium">状态</th>
                    <th scope="col" className="py-2 pr-3 font-medium">等级</th>
                    <th scope="col" className="py-2 pr-3 text-right font-medium">生词</th>
                    <th scope="col" className="py-2 pr-3 font-medium">注册</th>
                    <th scope="col" className="py-2 font-medium">最近登录</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((user) => {
                    const controls = renderControls(user);
                    return (
                      <tr key={user.id} className="border-b align-middle last:border-b-0">
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium">{user.displayName}</span>
                            {controls.isSelf && <Badge tone="primary">你</Badge>}
                          </div>
                          <div className="text-[11px] break-all text-muted-foreground">{user.email ?? '—'}</div>
                        </td>
                        <td className="py-2 pr-3">{controls.role}</td>
                        <td className="py-2 pr-3">{controls.status}</td>
                        <td className="py-2 pr-3">{user.currentCefr ?? '—'}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{user.wordCount}</td>
                        <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{formatDate(user.createdAt)}</td>
                        <td className="py-2 whitespace-nowrap tabular-nums">
                          {user.lastLoginAt === null ? '—' : formatDate(user.lastLoginAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 窄屏：卡片 */}
            <ul className="space-y-3 md:hidden">
              {items.map((user) => {
                const controls = renderControls(user);
                return (
                  <li key={user.id} className="rounded-lg border p-3 text-xs">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-semibold">{user.displayName}</span>
                      {controls.isSelf && <Badge tone="primary">你</Badge>}
                      <span className="ml-auto text-muted-foreground">{user.currentCefr ?? '未评估'}</span>
                    </div>
                    <p className="mt-0.5 break-all text-muted-foreground">{user.email ?? '未绑定邮箱'}</p>

                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <label className="space-y-1">
                        <span className="text-muted-foreground">角色</span>
                        {controls.role}
                      </label>
                      <label className="space-y-1">
                        <span className="text-muted-foreground">状态</span>
                        {controls.status}
                      </label>
                    </div>

                    <p className="mt-2 text-[11px] text-muted-foreground">
                      生词 {user.wordCount} · 注册 {formatDate(user.createdAt)} · 最近登录{' '}
                      {user.lastLoginAt === null ? '—' : formatDate(user.lastLoginAt)}
                    </p>
                  </li>
                );
              })}
            </ul>

            <Pagination page={page} totalPages={list.data?.totalPages ?? 0} onChange={setPage} />
          </>
        )}
      </CardBody>

      {/* 停用/注销的二次确认：下拉框改成即生效的话，一次误触就能让一个学生当天进不来 */}
      <Dialog
        open={statusChange !== null}
        onClose={() => setStatusChange(null)}
        title={
          statusChange === null
            ? '确认修改账号状态？'
            : `把「${statusChange.user.displayName}」设为${STATUS_LABELS[statusChange.next] ?? statusChange.next}？`
        }
        footer={
          <>
            <Button variant="outline" onClick={() => setStatusChange(null)}>
              取消
            </Button>
            <Button
              variant={statusChange?.next === 'deleted' ? 'destructive' : 'primary'}
              loading={update.isPending}
              onClick={() => {
                if (statusChange) update.mutate({ id: statusChange.user.id, patch: { status: statusChange.next } });
              }}
            >
              确认修改
            </Button>
          </>
        }
      >
        {statusChange && (
          <div className="space-y-2 text-sm">
            <p>
              账号：{statusChange.user.displayName}
              {statusChange.user.email ? `（${statusChange.user.email}）` : ''}
            </p>
            {statusChange.next === 'disabled' ? (
              <p className="text-xs text-muted-foreground">
                停用后该用户<b>立刻无法登录</b>（已登录的会话在下次请求时被拒）。
                学习数据全部保留，把状态改回「正常」即可恢复。
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                「已注销」标记用于配合用户自己的注销请求或合规处理。它不会删除数据（真正删除由
                「个人中心 → 彻底删除我的数据」完成），但用户无法登录；改回「正常」可恢复。
              </p>
            )}
          </div>
        )}
      </Dialog>
    </Card>
  );
}

function formatDate(epochMs: number): string {
  if (!Number.isFinite(epochMs) || epochMs <= 0) return '—';
  return new Date(epochMs).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
}
